import {
  Button,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PlaylistAddRounded, UpdateRounded } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import {
  createDocumentGroupAsync,
  getDocumentGroupAsync,
  updateDocumentGroupAsync
} from './documentGroupSlice';
import loadingSlice from '../../Loading/loadingSlice';
import {
  CreateDocumentGroup,
  UpdateDocumentGroup
} from '../../../api/document-groups';
import { Autocomplete } from '@material-ui/lab';
import { Discipline } from '../Discipline/disciplineSlice';

interface CreateEditDocumentGroup {
  id: number | null;
  name: string | null;
  order: number | null;
  discipline: Discipline | null;
}

export default function DocumentGroupCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedDocumentGroup } = useSelector(
    (state: RootState) => state.documentGroups
  );
  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );

  const params = useParams<{ documentGroupId: string }>();
  const documentGroupId = Number.parseInt(params.documentGroupId, 10);
  const isNewPage = !Boolean(documentGroupId);

  const [documentGroup, setDocumentGroup] = useState<CreateEditDocumentGroup>({
    id: null,
    name: null,
    order: null,
    discipline: null
  });

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedDocumentGroup) {
      setDocumentGroup({
        ...documentGroup,
        id: selectedDocumentGroup.id,
        name: selectedDocumentGroup.name,
        order: selectedDocumentGroup.order,
        discipline:
          disciplines.find(
            (discipline) => discipline.id === selectedDocumentGroup.disciplineId
          ) ?? null
      });
    }
  }, [selectedDocumentGroup, disciplines]);

  useEffect(() => {
    if (!isNewPage) {
      dispatch(getDocumentGroupAsync(documentGroupId));
    }
  }, []);

  function onChangeField<K extends keyof CreateEditDocumentGroup>(
    value: CreateEditDocumentGroup[K],
    field: K
  ): void {
    setDocumentGroup({
      ...documentGroup,
      [field]: value === '' ? null : value
    });
  }

  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();
    try {
      setLoading(true);
      if (
        documentGroup.name &&
        documentGroup.order &&
        documentGroup.discipline
      ) {
        if (isNewPage) {
          // create document group
          const createDocumentGroup: CreateDocumentGroup = {
            name: documentGroup.name,
            order: Number.parseInt(documentGroup.order.toString()),
            disciplineId: documentGroup.discipline.id
          };
          await dispatch(
            createDocumentGroupAsync(createDocumentGroup)
          ).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('documentGroupCreateEdit.messages.createSuccess')
            })
          );
        } else {
          // update document group
          if (documentGroup.id) {
            const updateDocumentGroup: UpdateDocumentGroup = {
              id: documentGroup.id,
              name: documentGroup.name,
              order: Number.parseInt(documentGroup.order.toString()),
              disciplineId: documentGroup.discipline.id
            };
            await dispatch(
              updateDocumentGroupAsync(updateDocumentGroup)
            ).unwrap();
            dispatch(
              loadingSlice.actions.success({
                message: t('documentGroupCreateEdit.messages.updateSuccess')
              })
            );
          }
        }
      } else {
        throw new Error(t('documentGroupCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('documentGroupCreateEdit.breadcrumb.masterDataName'),
          masterDataRef: 'document-groups',
          masterDataSubName: selectedDocumentGroup?.name,
          masterDataSubRef: selectedDocumentGroup?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('documentGroupCreateEdit.title.new') : t('documentGroupCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('documentGroupCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <div className={classes.row}>
            <TextField
              className={classes.columnLeft}
              id="order"
              label={t('documentGroupCreateEdit.labels.order')}
              size="small"
              variant="outlined"
              name="order"
              type="number"
              fullWidth
              value={documentGroup.order || ''}
              onChange={(event) =>
                onChangeField(
                  Math.max(
                    0,
                    Math.floor(Number.parseFloat(event.target.value))
                  ) || null, // Force the order to be positive integer number (1, 2, 3, ...) or null
                  'order'
                )
              }
              required
            />
            <TextField
              className={classes.columnRight}
              id="document-group-name"
              label={t('documentGroupCreateEdit.labels.name')}
              size="small"
              variant="outlined"
              name="name"
              fullWidth
              value={documentGroup.name || ''}
              onChange={(event) => onChangeField(event.target.value, 'name')}
              required
            />
          </div>
          <div className={classes.row}>
            <Autocomplete
              id="discipline"
              size="small"
              options={disciplines}
              getOptionLabel={(option) => option?.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('documentGroupCreateEdit.labels.discipline')}
                  required
                />
              )}
              value={documentGroup.discipline}
              onChange={(_, newValue) => onChangeField(newValue, 'discipline')}
              fullWidth
            />
          </div>
          <Button
            className={classes.submitButton}
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
            disabled={loading}
          >
            {isNewPage ? t('documentGroupCreateEdit.buttons.add') : t('documentGroupCreateEdit.buttons.update')}
          </Button>
        </Paper>
      </form>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    },
    row: {
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    columnLeft: {
      width: '50%',
      marginRight: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        marginRight: 0
      }
    },
    columnRight: {
      width: '50%',
      marginLeft: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        marginLeft: 0
      }
    },
    submitButton: {
      marginTop: theme.spacing(1.5)
    }
  })
);
